
import { useEffect } from 'react';
import { SimulationConfig } from '@/types';
import { apiFetch } from '@/lib/api';

export const useStudioHeartbeat = (user: any, configRef: React.MutableRefObject<SimulationConfig>, isPlaying: boolean) => {
  useEffect(() => {
    if (!user) return;

    const tracker = {
      startTime: Date.now(),
      lastFlush: Date.now(),
      modeStats: {} as Record<string, number>,
      studioTime: 0
    };

    const flushStats = (isClosing = false) => {
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - tracker.lastFlush) / 1000);
      
      if (!isClosing && document.visibilityState === 'hidden') return;
      if (elapsedSeconds <= 0) return;

      tracker.studioTime += elapsedSeconds;

      const currentMode = configRef.current.mode;
      if (isPlaying) {
        tracker.modeStats[currentMode] = (tracker.modeStats[currentMode] || 0) + elapsedSeconds;
      }

      // Instead of fetch, we accumulate in localStorage to be pushed with next save
      try {
        const stored = localStorage.getItem('flush_stats_queue');
        const queue = stored ? JSON.parse(stored) : { studioTime: 0, modeStats: {} };
        
        queue.studioTime += tracker.studioTime;
        Object.entries(tracker.modeStats).forEach(([mode, seconds]) => {
            queue.modeStats[mode] = (queue.modeStats[mode] || 0) + seconds;
        });
        
        localStorage.setItem('flush_stats_queue', JSON.stringify(queue));
      } catch (e) {
        console.error('Heartbeat: Failed to queue stats', e);
      }

      tracker.lastFlush = now;
      tracker.studioTime = 0;
      tracker.modeStats = {};
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushStats(true);
      } else {
        tracker.lastFlush = Date.now();
      }
    };

    const handleBeforeUnload = () => {
      flushStats(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handleBeforeUnload);
    window.addEventListener('beforeunload', handleBeforeUnload);

    const fallbackInterval = setInterval(flushStats, 5 * 60 * 1000);

    return () => {
      flushStats(true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handleBeforeUnload);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(fallbackInterval);
    };
  }, [user, isPlaying]);
};
